import { useState } from 'react';
import { REACT_APP_DISCORD_BOT_ENDPOINT } from '../config/constants';

// Adicionando import para acessar variáveis de ambiente
const PROVIDERS = (() => {
  try {
    // process.env.REACT_APP_PROVIDER pode vir com aspas simples ou duplas, então normalizamos
    let raw = process.env.REACT_APP_PROVIDER || '';
    if (raw.startsWith("'")) raw = raw.slice(1, -1); // remove aspas simples externas
    return JSON.parse(raw);
  } catch {
    return [];
  }
})();

// Providers que representam ambientes compartilhados (ex: runcloud) e não exigem tamanho de instância/disco
const SHARED_PROVIDERS = (() => {
  try {
    let raw = process.env.REACT_APP_PROVIDER_SHARED || '[]';
    if (raw.startsWith("'")) raw = raw.slice(1, -1);
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
})();

// Lista final de providers visíveis no select (todos os providers conhecidos)
const ALL_PROVIDERS = Array.from(new Set([...PROVIDERS, ...SHARED_PROVIDERS]));

export default function CreateVPS({ authToken }) {
  const [submitted, setSubmitted] = useState(false);
  const [responseMsg, setResponseMsg] = useState('');

  const [hostName, setHostName] = useState('');
  const [selectedCloudProvider, setSelectedCloudProvider] = useState('');
  const [selectedVPSSize, setSelectedVPSSize] = useState('');
  const [selectedDiskSize, setSelectedDiskSize] = useState('');
  const [email, setEmail] = useState('');
  const [proxyType, setProxyType] = useState('none');
  const [idVenda, setIdVenda] = useState('');
  const [ClientName, setClientName] = useState('');
  const [bancoDeDadosPrivado, setBancoDeDadosPrivado] = useState(false);
  const [firewallModSec, setFirewallModSec] = useState(true);
  const [emailError, setEmailError] = useState('');

  // Helper para saber se o provider atual é compartilhado (runcloud, etc)
  const isSharedProvider = SHARED_PROVIDERS.includes(selectedCloudProvider);

  // Funções para tratar mudanças nos selects
  const handleCloudProviderChange = (e) => {
    const value = e.target.value;
    setSelectedCloudProvider(value);

    // Se mudou para um provider "shared", limpamos os campos de tamanho
    // pois não fazem sentido nesse contexto
    if (SHARED_PROVIDERS.includes(value)) {
      setSelectedVPSSize('');
      setSelectedDiskSize('');
    }
  };

  const handleVPSSizeChange = (e) => {
    const vpsSize = e.target.value;
    setSelectedVPSSize(vpsSize);
    
    // Seleciona automaticamente o tamanho do disco baseado no tamanho da VPS
    let diskSize = '';
    if (vpsSize === '1GB' || vpsSize === '2GB') {
      diskSize = '40GB';
    } else if (vpsSize === '4GB') {
      diskSize = '60GB';
    } else if (vpsSize === '8GB') {
      diskSize = '80GB';
    }
    
    if (diskSize) {
      setSelectedDiskSize(diskSize);
    }
  };

  const handleDiskSizeChange = (e) => {
    setSelectedDiskSize(e.target.value);
  };

  // Substituir CloudProvider fixo pelo dinâmico
  // const CloudProvider = [ ... ];
  const CloudProvider = ALL_PROVIDERS.map((provider) => ({
    text: provider,
    value: provider,
    selected: false
  }));

  const VPSSize = [
    { "text": "1GB","value": "1GB","selected": false},
    { "text": "2GB","value": "2GB","selected": false},
    { "text": "4GB","value": "4GB","selected": false},
    { "text": "8GB","value": "8GB","selected": false},
  ]

  const DiskSize = [
    { "text": "10GB","value": "10GB","selected": false},
    { "text": "40GB","value": "40GB","selected": false},
    { "text": "60GB","value": "60GB","selected": false},
    { "text": "80GB","value": "80GB","selected": false},
    { "text": "90GB","value": "90GB","selected": false},
    { "text": "100GB","value": "100GB","selected": false},
    { "text": "120GB","value": "120GB","selected": false},
    { "text": "300GB","value": "300GB","selected": false},
  ]

  const ProxyOptions = [
    { "text": "Não ter proxy","value": "none","selected": false},
    { "text": "Gocache","value": "gocache","selected": false},
    { "text": "CloudFlare","value": "cloudflare","selected": false},
  ]

  function isValidEmail(email) {
    // Regex simples para validação de email
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  // Função para separar hostname (agora aceita URLs completas e trata TLDs compostos)
  function parseHostname(input) {
    input = input.trim().toLowerCase();
    let fullhostname = input;
    try {
      if (input.startsWith('http://') || input.startsWith('https://')) {
        fullhostname = new URL(input).hostname;
      } else if (input.includes('/')) {
        fullhostname = input.split('/')[0];
      }
    } catch (e) {
      fullhostname = input;
    }

    const tldsCompostos = [
      'com.br', 'net.br', 'org.br', 'gov.br', 'edu.br'
      // Adicione outros TLDs compostos conforme necessário
    ];

    const parts = fullhostname.split('.');
    let root = '';
    let subdomain = '';

    if (parts.length < 2) {
      root = fullhostname;
      subdomain = '';
    } else {
      const lastTwo = parts.slice(-2).join('.');
      const lastThree = parts.slice(-3).join('.');
      if (tldsCompostos.includes(lastTwo)) {
        root = parts.slice(-3).join('.');
        subdomain = parts.slice(0, -3).join('.') || '';
      } else if (tldsCompostos.includes(lastThree)) {
        root = parts.slice(-4).join('.');
        subdomain = parts.slice(0, -4).join('.') || '';
      } else {
        root = parts.slice(-2).join('.');
        subdomain = parts.slice(0, -2).join('.') || '';
      }
    }

    return {
      fullhostname,
      subdomain,
      root
    };
  }

  // Função para gerar hash aleatório
  function generateRandomHash(length = 10) {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    // Validação do email
    if (!isValidEmail(email)) {
      setEmailError('Por favor, insira um email válido.');
      return;
    } else {
      setEmailError('');
    }
    const { fullhostname, subdomain, root } = parseHostname(hostName);
    // Se ClientName estiver vazio, gera hash
    const clientNameToSend = ClientName.trim() === '' ? generateRandomHash(10) : ClientName;
    const payload = {
      form: 'create_vps',
      cloudProvider: selectedCloudProvider,
      vpsSize: selectedVPSSize,
      diskSize: selectedDiskSize,
      fullhostname,
      subdomain,
      root,
      ClientName: clientNameToSend,
      email,
      proxyType,
      idVenda,
      bancoDeDadosPrivado,
      firewallModSec
    };
    fetch(REACT_APP_DISCORD_BOT_ENDPOINT, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({ 
        command: 'submit',
        data: payload
      }),
    })
      .then((res) => res.text())
      .then((msg) => {
        setSubmitted(true);
        setResponseMsg(msg);
      });
  };

  if (submitted) {
    let content;
    try {
      const parsed = JSON.parse(responseMsg);
      content = (
        <div className="json-response-list">
          {Object.entries(parsed).map(([key, value]) => (
            <div key={key} style={{ marginBottom: '0.5em' }}>
              <strong>{key}:</strong> <span>{String(value)}</span>
            </div>
          ))}
        </div>
      );
    } catch {
      content = <p>{responseMsg}</p>;
    }
    return (
      <div className="dynamic-form">
        <div className="success-message">
          {content}
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="dynamic-form">

     <div className="form-group">
        <label className="form-label">Cloud Provider</label>
        <select 
          value={selectedCloudProvider} 
          onChange={handleCloudProviderChange}
          className="form-select"
        >
          <option value="">Selecione o Cloud Provider</option>
          {CloudProvider.map((CloudProvider) => (
            <option key={CloudProvider.value} value={CloudProvider.value}>
              {CloudProvider.text}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label">ID da Venda</label>
        <input
          type="text"
          value={idVenda}
          onChange={(e) => setIdVenda(e.target.value)}
          className="form-input"
        />
      </div>

      <div className="form-group">
        <label className="form-label">Nome do Cliente (Opcional)</label>
        <input
          type="text"
          value={ClientName}
          onChange={(e) => setClientName(e.target.value)}
          className="form-input"
        />
      </div>
  
      <div className="form-group">
        <label className="form-label">Email</label>
        <input
          type="text"
          value={email}
          onChange={(e) => setEmail(e.target.value)}  
          className="form-input"
        />
        {emailError && (
          <div style={{ color: 'red', marginTop: 4 }}>{emailError}</div>
        )}
      </div>
      <div className="form-group">
        <label className="form-label">Nome do Host</label>
        <input
          type="text"
          value={hostName}
          onChange={(e) => setHostName(e.target.value)}
          className="form-input"
        />
      </div>
  
      {!isSharedProvider && (
        <>
          <div className="form-group">
            <label className="form-label">Tamanho da Instância</label>
            <select 
              value={selectedVPSSize} 
              onChange={handleVPSSizeChange}
              className="form-select"
            >
              <option value="">Selecione o Tamanho da Instância</option>
              {VPSSize.map((VPSSize) => (
                <option key={VPSSize.value} value={VPSSize.value}>
                  {VPSSize.text}
                </option>
              ))}
            </select>
          </div>
      
      
          <div className="form-group">
            <label className="form-label">Tamanho do Disco</label>
            <select 
              value={selectedDiskSize} 
              onChange={handleDiskSizeChange}
              className="form-select"
            >   
              <option value="">Selecione o Tamanho do Disco</option>
              {DiskSize.map((DiskSize) => (
                <option key={DiskSize.value} value={DiskSize.value}>
                  {DiskSize.text}
                </option>
              ))}
            </select>
          </div>
        </>
      )}

      <div className="form-group">
        <label className="form-label">Tipo de Proxy</label>
        <select 
          value={proxyType} 
          onChange={(e) => setProxyType(e.target.value)}
          className="form-select"
        >
          {ProxyOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.text}
            </option>
          ))}
        </select>
      </div>

      {!isSharedProvider && (
        <>
          <div className="form-group">
            <label className="form-label checkbox-label">
              <input
                type="checkbox"
                checked={firewallModSec}
                onChange={(e) => setFirewallModSec(e.target.checked)}
                className="form-checkbox"
              />
              <span className="checkbox-text">Firewall (ModSec)</span>
            </label>
          </div>

          <div className="form-group">
            <label className="form-label checkbox-label">
              <input
                type="checkbox"
                checked={bancoDeDadosPrivado}
                onChange={(e) => setBancoDeDadosPrivado(e.target.checked)}
                className="form-checkbox"
              />
              <span className="checkbox-text">Banco de Dados Interno(Localhost)</span>
            </label>
          </div>
        </>
      )}

      <div className="form-actions">
        <button 
          type="submit" 
          disabled={
            !selectedCloudProvider ||
            !email ||
            !idVenda ||
            !hostName ||
            // Para providers não compartilhados, continua exigindo tamanho de instância e disco
            (!isSharedProvider && (!selectedVPSSize || !selectedDiskSize))
          }
          className="submit-button"
        >
          Enviar o comando
        </button>
      </div>

      {submitted && (
        <div className="success-message">
          <span className="success-icon">✓</span>
          <p>Linha de Comando executada com sucesso!</p>
        </div>
      )}
    </form>
  );
} 